import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch, clearStoredCredentials, getAuthHeaders } from '../../utils/api';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';

import Header from '../../frontend/components/Header';
import Footer from '../../frontend/components/Footer';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:7018';
const ITEMS_PER_PAGE = 6;

const formatContentList = (content) => {
  if (!content) return [];
  return content.split('\n').map(line => line.trim()).filter(line => line.length > 0);
};

const convertBase64ToBlob = (base64Data, contentType = 'application/pdf') => {
  const base64 = base64Data.replace(/^data:application\/pdf;base64,/, '');
  const binary = atob(base64);
  const len = binary.length;
  const buffer = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    buffer[i] = binary.charCodeAt(i);
  }
  return new Blob([buffer], { type: contentType });
};

const downloadPDF = async (policyDocument, fileName = 'policy-document.pdf') => {
  if (!policyDocument) {
    toast.error('No policy document available to download');
    return;
  }
  try {
    let blob = null;
    let finalFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;

    if (policyDocument.startsWith('data:application/pdf;base64,') ||
        (/^[A-Za-z0-9+/=]+$/.test(policyDocument) && policyDocument.length > 100)) {
      const base64 = policyDocument.startsWith('data:')
        ? policyDocument
        : `data:application/pdf;base64,${policyDocument}`;
      blob = convertBase64ToBlob(base64);
    } else {
      const fileUrl = policyDocument.startsWith('http')
        ? policyDocument
        : `${API_BASE.replace(/\/api$/, '')}/policy-documents/${policyDocument}`;
      
      const authHeaders = getAuthHeaders();
      const response = await fetch(fileUrl, { method: 'GET', headers: authHeaders || {} });
      if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
      blob = await response.blob();
      
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition?.includes('filename=')) {
        const match = contentDisposition.match(/filename=["']?([^"';]+)["']?/);
        if (match?.[1]) finalFileName = match[1];
      }
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Download started');
  } catch (error) {
    console.error('❌ PDF download error:', error);
    toast.error(error.message || 'Failed to download policy document');
  }
};

const viewPDF = async (policyDocument) => {
  if (!policyDocument) {
    toast.error('No policy document available to view');
    return;
  }
  try {
    let url = null;
    if (policyDocument.startsWith('data:application/pdf;base64,') ||
        (/^[A-Za-z0-9+/=]+$/.test(policyDocument) && policyDocument.length > 100)) {
      const base64 = policyDocument.startsWith('data:')
        ? policyDocument
        : `data:application/pdf;base64,${policyDocument}`;
      const blob = convertBase64ToBlob(base64);
      url = URL.createObjectURL(blob);
    } else {
      const fileUrl = policyDocument.startsWith('http')
        ? policyDocument
        : `${API_BASE.replace(/\/api$/, '')}/policy-documents/${policyDocument}`;
      const authHeaders = getAuthHeaders();
      const response = await fetch(fileUrl, { method: 'GET', headers: authHeaders || {} });
      if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
      const blob = await response.blob();
      url = URL.createObjectURL(blob);
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch (error) {
    console.error('❌ PDF view error:', error);
    toast.error(error.message || 'Failed to open policy document');
  }
};

function AllPlans() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [selectedTiers, setSelectedTiers] = useState({});
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isViewingPdf, setIsViewingPdf] = useState(false);
  
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const selectedPlanCategoryId = searchParams.get('categoryId');
  const selectedCategoryName = searchParams.get('categoryName');

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const requestBody = selectedPlanCategoryId 
        ? { planCategoryId: parseInt(selectedPlanCategoryId, 10) } 
        : {};

      const response = await authenticatedFetch(`${API_BASE}/products/products`, 'POST', requestBody);
      const isSuccess = response?.success === true || response?.status === 'success';

      if (isSuccess) {
        const apiData = response.data || response.result || [];
        
        const formatted = apiData.map(item => ({
          id: item.id,
          name: item.name,
          description: item.benefits?.split('\n')[0] || item.howItWorks?.substring(0, 100) || '',
          category: item.categoryName,
          planCategory: item.planCategoryName,
          categoryId: item.categoryId,
          planCategoryId: item.planCategoryId,
          provider: item.providerName,
          providerId: item.providerId,
          amounts: item.amounts,
          familyType: item.familyType,
          numberOfFamilyMembers: item.numberOfFamilyMembers,
          isRequireRegister: item.isRequireRegister,
          status: item.status,
          createdAt: item.createdAt,
          policyDocument: item.policyDocument,
          benefits: item.benefits,
          howItWorks: item.howItWorks,
          howToClaim: item.howToClaim,
          hospitals: item.hospitals || [],
          planServices: item.planServices || [],
          planServiceDescriptions: item.planServiceDescriptions || [],
          costAnalysis: item.costAnalysis || [],
          serviceDetails: item.serviceDetails || [] 
        }));

        setProducts(formatted);
        const pages = Math.ceil(formatted.length / ITEMS_PER_PAGE);
        setTotalPages(pages > 0 ? pages : 1);
      } else if (response?.success === false || response?.status === 'fail' || response?.status === 'error') {
        const msg = (response?.message || '').toLowerCase();
        if (msg.includes('expired') || msg.includes('unauthorized') || msg.includes('not found')) {
          clearStoredCredentials();
          localStorage.removeItem('userSession');
          toast.warning(response.message || 'Session expired');
          navigate('/login', { replace: true });
          return;
        }
        throw new Error(response.message || 'Failed to fetch products');
      } else {
        throw new Error('Invalid API response');
      }
    } catch (err) {
      console.error('💥 Products fetch error:', err);
      const errorMsg = err.message?.toLowerCase() || '';
      
      if (errorMsg.includes('failed to fetch') || err.name === 'TypeError') {
        setError('Cannot connect to server');
        toast.error('Cannot connect to server. Check your internet.');
      } else if (errorMsg.includes('401') || errorMsg.includes('expired')) {
        clearStoredCredentials();
        localStorage.removeItem('userSession');
        toast.warning('Session expired. Please log in again.');
        navigate('/login', { replace: true });
      } else if (errorMsg.includes('404')) {
        setError('API endpoint not found');
        toast.error('API endpoint not found');
      } else {
        setError(err.message || 'Could not load products');
        toast.error(err.message || 'Could not load products');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, selectedPlanCategoryId]);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true, offset: 100 });
    fetchProducts();
    setCurrentPage(1);
  }, [fetchProducts]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return products.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [products, currentPage]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    document.getElementById('all-products')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const openProductModal = (product) => {
    setSelectedProduct(product);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeProductModal = () => {
    setModalOpen(false);
    setSelectedProduct(null);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && modalOpen) closeProductModal();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [modalOpen]);

  const handleViewAll = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    document.getElementById('all-products')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleClearFilter = () => {
    setSearchParams({});
    setCurrentPage(1);
  };

  const hasService = (product, serviceName) => {
    if (!product.serviceDetails) return false;
    return product.serviceDetails.some(detail => 
      detail.serviceName?.toLowerCase().includes(serviceName.toLowerCase())
    );
  };

  const getServiceNarration = (product, serviceName) => {
    if (!product.serviceDetails) return null;
    const detail = product.serviceDetails.find(d => 
      d.serviceName?.toLowerCase().includes(serviceName.toLowerCase())
    );
    return detail?.naration || null;
  };

  const getValidCostAnalysis = (costAnalysis) => {
    if (!costAnalysis) return [];
    return costAnalysis.filter(cost => cost.amounts && Number(cost.amounts) > 0);
  };

  const handleTierChange = (productId, value) => {
    setSelectedTiers(prev => ({ ...prev, [productId]: value }));
  };

  const getEnrollmentRoute = (planType) => {
    if (!planType) return '/enrollment/myself';
    const type = planType.toLowerCase();
    if (type.includes('individual') || type.includes('myself') || type.includes('single')) {
      return '/enrollment/myself';
    }
    return '/enrollment/family-group';
  };

  return (
    <>
      <Header />

      <section id="products" className="medolia-service-sec pt-130 pb-80" style={{ background: '#fafafa' }}>
        <ToastContainer position="top-right" autoClose={3000} theme="light" />
        
        <div className="container">
          <div className="row align-items-center mb-4">
            <div className="col-12 mb-3">
              <button 
                onClick={() => navigate(-1)} 
                style={{
                  background: 'transparent',
                  color: '#4b5563',
                  padding: '8px 20px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  border: '2px solid #e5e7eb',
                  cursor: 'pointer',
                  fontSize: '0.95rem'
                }}
                onMouseOver={(e) => { 
                  e.currentTarget.style.borderColor = '#F89423'; 
                  e.currentTarget.style.color = '#F89423'; 
                }}
                onMouseOut={(e) => { 
                  e.currentTarget.style.borderColor = '#e5e7eb'; 
                  e.currentTarget.style.color = '#4b5563'; 
                }}
              >
                <i className="fas fa-arrow-left"></i> Go Back to Categories
              </button>
            </div>
            
            <div className="col-lg-6">
              <div className="section-title mb-50 text-center text-lg-start" data-aos="fade-up" data-aos-duration="1000">
                <span className="sub-title text-uppercase fw-semibold" style={{ color: '#F89423', letterSpacing: '2px' }}>
                  {selectedCategoryName ? `${selectedCategoryName} Plans` : 'Our Plans'}
                </span>
                <h2 className="mt-2" style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937' }}>
                  {selectedCategoryName ? `Explore our ${selectedCategoryName} health plans` : 'Find the Perfect Health Plan for You'}
                </h2>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="medolia-button float-lg-end mb-60 text-center text-lg-end" data-aos="fade-up" data-aos-duration="1200">
                <a 
                  href="#all-products" 
                  className="theme-btn style-one" 
                  onClick={handleViewAll}
                  style={{
                    backgroundColor: '#F89423', color: 'white', padding: '12px 32px', borderRadius: '8px',
                    textDecoration: 'none', fontWeight: '600', display: 'inline-flex', alignItems: 'center',
                    gap: '8px', transition: 'all 0.3s ease', border: '2px solid #F89423', cursor: 'pointer'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#ea7c1f'; e.currentTarget.style.borderColor = '#ea7c1f'; }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#F89423'; e.currentTarget.style.borderColor = '#F89423'; }}
                >
                  <i className="fas fa-th-large me-2"></i>View All Products
                </a>
              </div>
            </div>
          </div>

          {loading && (
            <div className="row justify-content-center">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div className="col-xl-4 col-md-6" key={i}>
                  <div className="medolia-service-box mb-40" style={{ 
                    background: 'white', borderRadius: '16px', padding: '35px', 
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)', minHeight: '520px', display: 'flex', flexDirection: 'column',
                    border: '1px solid #f0f0f0', borderTop: '4px solid #F89423'
                  }}>
                    <div className="skeleton-title mb-3" style={{ width: '70%', height: '28px', borderRadius: '6px', background: '#f3f4f6', margin: '0 auto 20px auto' }}></div>
                    <div style={{ height: '2px', background: '#f3f4f6', margin: '0 0 25px 0' }}></div>
                    {[1, 2, 3, 4, 5].map(j => (
                      <div key={j} className="skeleton-benefit mb-3" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#f3f4f6' }}></div>
                        <div style={{ flex: 1, height: '16px', borderRadius: '4px', background: '#f3f4f6' }}></div>
                      </div>
                    ))}
                    <div style={{ marginTop: 'auto', paddingTop: '20px' }}>
                      <div className="skeleton-btn" style={{ width: '100%', height: '48px', borderRadius: '8px', background: '#f3f4f6' }}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && !loading && (
            <div className="alert alert-warning text-center" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
              <button className="btn btn-sm btn-outline-warning ms-3" onClick={fetchProducts}>
                <i className="fas fa-redo me-1"></i>Retry
              </button>
            </div>
          )}

          {!loading && !error && products.length === 0 && (
            <div className="text-center py-5">
              <i className="fas fa-box-open fa-4x text-muted mb-3"></i>
              <h5 className="text-muted">
                {selectedCategoryName ? `No ${selectedCategoryName} plans available` : 'No products available'}
              </h5>
              <p className="text-muted small">Check back later or contact support.</p>
              {selectedCategoryName && (
                <button onClick={handleClearFilter} className="btn btn-outline-primary mt-3">
                  View All Plans
                </button>
              )}
            </div>
          )}

          {/* ✅ 2. CARDS VIEW */}
          {!loading && !error && products.length > 0 && (
            <div className="row justify-content-center" id="all-products">
              <div className="col-12 mb-4">
                <h4 className="mb-0" style={{ fontWeight: '700', color: '#1f2937', fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <i className="fas fa-th-large" style={{ color: '#F89423' }}></i>
                  Individual Plan Details
                </h4>
              </div>
              
              {paginatedProducts.map((product, index) => {
                const validCosts = getValidCostAnalysis(product.costAnalysis);
                const defaultTier = validCosts.length > 0 ? validCosts[0].familyType : 'Standard';
                const currentTier = selectedTiers[product.id] || defaultTier;
                const enrollmentRoute = getEnrollmentRoute(currentTier);
                const isIndividual = currentTier.toLowerCase().includes('individual') || currentTier.toLowerCase().includes('myself');
                
                return (
                  <div className="col-xl-4 col-md-6" key={product.id || index}>
                    <div 
                      className="medolia-service-box mb-40" 
                      data-aos="fade-up" 
                      data-aos-duration={1000 + (index * 100)}
                      style={{
                        background: 'white', 
                        borderRadius: '16px', 
                        padding: '0',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s ease',
                        border: '1px solid #f0f0f0', 
                        borderTop: '4px solid #F89423',
                        position: 'relative', 
                        overflow: 'hidden', 
                        minHeight: 'auto', 
                        display: 'flex', 
                        flexDirection: 'column'
                      }}
                    >
                      <div style={{ padding: '30px 30px 20px 30px', background: 'linear-gradient(135deg, #fff7ed 0%, #fff 100%)' }}>
                        <h4 style={{ 
                          fontWeight: '700', 
                          color: '#1f2937', 
                          fontSize: '1.4rem', 
                          lineHeight: '1.3',
                          textAlign: 'center',
                          marginBottom: '15px'
                        }}>
                          {product.name || 'Unnamed Plan'}
                        </h4>
                        
                        {validCosts.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {validCosts.map((cost, idx) => (
                              <div key={idx} style={{ 
                                background: 'white', 
                                borderRadius: '8px', 
                                padding: '12px 16px', 
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                border: currentTier === cost.familyType ? '2px solid #F89423' : '1px solid #fed7aa',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer'
                              }}
                              onClick={() => handleTierChange(product.id, cost.familyType)}
                              >
                                <div style={{ textAlign: 'left' }}>
                                  <div style={{ fontWeight: '700', color: '#1f2937', fontSize: '0.95rem' }}>
                                    {cost.familyType || 'Standard Plan'}
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                    {cost.numberOfFamilyMembers} Member{cost.numberOfFamilyMembers > 1 ? 's' : ''}
                                  </div>
                                </div>
                                <div style={{ fontWeight: '800', color: '#F89423', fontSize: '1.1rem' }}>
                                  ₦{Number(cost.amounts || 0).toLocaleString()}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', color: '#F89423', fontWeight: '700', fontSize: '1.5rem' }}>
                            ₦{Number(product.amounts || 0).toLocaleString()}
                          </div>
                        )}
                      </div>

                      {product.serviceDetails && product.serviceDetails.length > 0 && (
                        <div style={{ padding: '0 30px 20px 30px' }}>
                          {(() => {
                            const groupedDetails = product.serviceDetails.reduce((acc, detail) => {
                              const sName = detail.serviceName || 'General Service';
                              if (!acc[sName]) acc[sName] = [];
                              acc[sName].push(detail);
                              return acc;
                            }, {});

                            return Object.entries(groupedDetails).map(([serviceName, details], groupIndex) => (
                              <div key={groupIndex} className="mb-3">
                                <h6 
                                  className="fw-bold mb-2 px-2 py-1" 
                                  style={{ 
                                    color: '#F89423', 
                                    backgroundColor: '#fff7ed', 
                                    borderRadius: '6px',
                                    fontSize: '0.9rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}
                                >
                                  {serviceName}
                                </h6>
                                
                                <div className="rounded" style={{ border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                                  <table className="table table-sm mb-0" style={{ fontSize: '0.85rem' }}>
                                    <thead style={{ background: '#f9fafb' }}>
                                      <tr>
                                        <th style={{ fontWeight: '600', border: 'none', width: '50%', color: '#374151', padding: '8px 12px', fontSize: '0.8rem' }}>Service Description</th>
                                        <th style={{ fontWeight: '600', border: 'none', width: '50%', color: '#374151', padding: '8px 12px', fontSize: '0.8rem' }}>Narration</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {details.slice(0, 3).map((detail, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                          <td style={{ padding: '8px 12px', color: '#4b5563', verticalAlign: 'middle', fontSize: '0.85rem' }}>
                                            {detail.descriptionName || 'General Service'}
                                          </td>
                                          <td style={{ padding: '8px 12px', color: '#6b7280', verticalAlign: 'middle', lineHeight: '1.4', fontSize: '0.85rem' }}>
                                            {detail.naration || '—'}
                                          </td>
                                        </tr>
                                      ))}
                                      {details.length > 3 && (
                                        <tr>
                                          <td colSpan="2" style={{ padding: '6px 12px', textAlign: 'center', color: '#F89423', fontSize: '0.8rem' }}>
                                            + {details.length - 3} more services
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                      
                      <div style={{ padding: '0 30px 30px 30px', marginTop: 'auto' }}>
                        <button
                          onClick={() => openProductModal(product)}
                          style={{
                            width: '100%',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#F89423',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '0.95rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 12px rgba(248, 148, 35, 0.3)',
                            marginBottom: '12px'
                          }}
                          onMouseOver={(e) => { 
                            e.currentTarget.style.background = '#ea7c1f'; 
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseOut={(e) => { 
                            e.currentTarget.style.background = '#F89423'; 
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <i className="fas fa-list-ul"></i> View All Benefits
                        </button>
                        
                        {validCosts.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
                              Select Coverage Type:
                            </label>
                            <select 
                              value={currentTier}
                              onChange={(e) => handleTierChange(product.id, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                fontSize: '0.9rem',
                                color: '#374151',
                                backgroundColor: '#fff',
                                cursor: 'pointer',
                                outline: 'none'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#F89423'}
                              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                            >
                              {validCosts.map((cost, idx) => (
                                <option key={idx} value={cost.familyType}>
                                  {cost.familyType} - ₦{Number(cost.amounts || 0).toLocaleString()} ({cost.numberOfFamilyMembers} Member{cost.numberOfFamilyMembers > 1 ? 's' : ''})
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <Link 
                          to={`${enrollmentRoute}?planId=${product.id}&planName=${encodeURIComponent(product.name)}&planType=${encodeURIComponent(currentTier)}`}
                          onClick={() => {
                            const selectedCost = validCosts.length > 0 
                              ? (validCosts.find(c => c.familyType === currentTier) || validCosts[0]) 
                              : null;
                            const planData = {
                              id: product.id,
                              name: product.name,
                              type: currentTier,
                              price: selectedCost ? selectedCost.amounts : product.amounts
                            };
                            sessionStorage.setItem('selectedPlan', JSON.stringify(planData));
                          }}
                          style={{
                            width: '100%',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            border: '2px solid #F89423',
                            background: 'transparent',
                            color: '#F89423',
                            fontWeight: '600',
                            fontSize: '0.95rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            transition: 'all 0.3s ease',
                            textDecoration: 'none'
                          }}
                          onMouseOver={(e) => { 
                            e.currentTarget.style.background = '#F89423'; 
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseOut={(e) => { 
                            e.currentTarget.style.background = 'transparent'; 
                            e.currentTarget.style.color = '#F89423';
                          }}
                        >
                          {isIndividual ? 'Get Started' : 'Buy Plan'}
                          <i className={`fas ${isIndividual ? 'fa-user-plus' : 'fa-shopping-cart'}`} style={{ fontSize: '14px' }}></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ✅ 1. COMPARISON TABLE */}
          {!loading && !error && products.length > 0 && (
            <div className="row mb-5" data-aos="fade-up">
              <div className="col-12">
                <h4 className="mb-4" style={{ fontWeight: '700', color: '#1f2937', fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <i className="fas fa-balance-scale" style={{ color: '#F89423' }}></i>
                  Plan Comparison
                </h4>
                <div className="table-responsive" style={{ 
                  background: 'white', 
                  borderRadius: '16px', 
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  overflow: 'auto'
                }}>
                  <table className="table table-bordered mb-0" style={{ minWidth: '1200px', fontSize: '0.9rem' }}>
                    <thead style={{ background: '#f9fafb' }}>
                      <tr>
                        <th style={{ 
                          width: '250px', 
                          fontWeight: '700', 
                          color: '#374151',
                          padding: '20px',
                          borderBottom: '3px solid #F89423',
                          position: 'sticky',
                          left: 0,
                          background: '#f9fafb',
                          zIndex: 10
                        }}>
                          BENEFIT CATEGORIES
                        </th>
                        {products.map((product, idx) => {
                          const validCostsTable = getValidCostAnalysis(product.costAnalysis);
                          const defaultTierTable = validCostsTable.length > 0 ? validCostsTable[0].familyType : 'Standard';
                          const currentTierTable = selectedTiers[product.id] || defaultTierTable;
                          const enrollmentRouteTable = getEnrollmentRoute(currentTierTable);
                          const isIndividualTable = currentTierTable.toLowerCase().includes('individual') || currentTierTable.toLowerCase().includes('myself');

                          return (
                            <th key={idx} style={{ 
                              width: '220px',
                              textAlign: 'center',
                              padding: '20px 15px',
                              borderBottom: '3px solid #F89423',
                              background: '#fff7ed',
                              verticalAlign: 'top'
                            }}>
                              <div style={{ fontWeight: '700', color: '#1f2937', marginBottom: '10px', fontSize: '1.1rem' }}>
                                {product.name}
                              </div>
                              
                              {validCostsTable.length > 0 && (
                                <div style={{ marginBottom: '12px' }}>
                                  <select 
                                    value={currentTierTable}
                                    onChange={(e) => handleTierChange(product.id, e.target.value)}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      borderRadius: '6px',
                                      border: '1px solid #e5e7eb',
                                      fontSize: '0.85rem',
                                      color: '#374151',
                                      backgroundColor: '#fff',
                                      cursor: 'pointer',
                                      marginBottom: '10px'
                                    }}
                                  >
                                    {validCostsTable.map((cost, cIdx) => (
                                      <option key={cIdx} value={cost.familyType}>
                                        {cost.familyType} - ₦{Number(cost.amounts || 0).toLocaleString()}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}

                              <Link 
                                to={`${enrollmentRouteTable}?planId=${product.id}&planName=${encodeURIComponent(product.name)}&planType=${encodeURIComponent(currentTierTable)}`}
                                onClick={() => {
                                  const selectedCostTable = validCostsTable.length > 0 
                                    ? (validCostsTable.find(c => c.familyType === currentTierTable) || validCostsTable[0]) 
                                    : null;
                                  const planData = {
                                    id: product.id,
                                    name: product.name,
                                    type: currentTierTable,
                                    price: selectedCostTable ? selectedCostTable.amounts : product.amounts
                                  };
                                  sessionStorage.setItem('selectedPlan', JSON.stringify(planData));
                                }}
                                style={{
                                  display: 'inline-block',
                                  padding: '8px 20px',
                                  borderRadius: '6px',
                                  background: '#F89423',
                                  color: 'white',
                                  textDecoration: 'none',
                                  fontWeight: '600',
                                  fontSize: '0.85rem',
                                  transition: 'all 0.2s'
                                }}
                                onMouseOver={(e) => {
                                  e.target.style.background = '#ea7c1f';
                                  e.target.style.transform = 'translateY(-2px)';
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.background = '#F89423';
                                  e.target.style.transform = 'translateY(0)';
                                }}
                              >
                                {isIndividualTable ? 'Get Started' : 'Buy Plan'} →
                              </Link>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ fontWeight: '600', color: '#374151', padding: '15px 20px', background: '#fafafa', verticalAlign: 'top' }}>Pricing Tiers</td>
                        {products.map((product, idx) => {
                          const validCosts = getValidCostAnalysis(product.costAnalysis);
                          return (
                            <td key={idx} style={{ textAlign: 'center', padding: '15px', verticalAlign: 'top' }}>
                              {validCosts.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {validCosts.map((cost, cIdx) => (
                                    <div key={cIdx} style={{ 
                                      background: '#fff', 
                                      padding: '10px', 
                                      borderRadius: '6px', 
                                      border: (selectedTiers[product.id] || validCosts[0].familyType) === cost.familyType ? '2px solid #F89423' : '1px solid #fed7aa',
                                      transition: 'all 0.2s'
                                    }}>
                                      <div style={{ fontWeight: '700', color: '#1f2937', fontSize: '0.85rem' }}>{cost.familyType || 'Standard'}</div>
                                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{cost.numberOfFamilyMembers} Member{cost.numberOfFamilyMembers > 1 ? 's' : ''}</div>
                                      <div style={{ fontWeight: '800', color: '#F89423', fontSize: '0.95rem', marginTop: '4px' }}>
                                        ₦{Number(cost.amounts || 0).toLocaleString()}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ fontWeight: '700', color: '#F89423', fontSize: '1.1rem' }}>
                                  ₦{Number(product.amounts || 0).toLocaleString()}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <td style={{ fontWeight: '600', color: '#374151', padding: '15px 20px', background: '#fafafa' }}>Region of Cover</td>
                        {products.map((product, idx) => (
                          <td key={idx} style={{ textAlign: 'center', padding: '15px', color: '#4b5563' }}>Nigeria</td>
                        ))}
                      </tr>

                      {(() => {
                        const allServices = new Set();
                        products.forEach(product => {
                          product.serviceDetails?.forEach(detail => {
                            if (detail.serviceName) allServices.add(detail.serviceName);
                          });
                        });

                        return Array.from(allServices).map((serviceName, serviceIdx) => (
                          <tr key={serviceIdx}>
                            <td style={{ fontWeight: '600', color: '#374151', padding: '15px 20px', background: '#fafafa' }}>
                              {serviceName}
                            </td>
                            {products.map((product, idx) => {
                              const hasSvc = product.serviceDetails?.some(d => d.serviceName === serviceName);
                              const narration = product.serviceDetails?.find(d => d.serviceName === serviceName)?.naration;
                              
                              return (
                                <td key={idx} style={{ textAlign: 'center', padding: '15px' }}>
                                  {hasSvc ? (
                                    <div>
                                      <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: '24px' }}></i>
                                      {narration && narration !== 'Covered' && (
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '5px' }}>
                                          {narration}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <i className="fas fa-times-circle" style={{ color: '#ef4444', fontSize: '24px' }}></i>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ));
                      })()}

                      {['GP Consultation', 'Specialist Consultation', 'Telemedicine Consultation', 'Basic Laboratory Investigations/X-Ray/Ultrasounds', 'Admission & Feeding', 'Emergency Services', 'Antenatal Care', 'Induction of Labour & Delivery', 'Eye Care', 'Network Gym Centres'].map((category) => (
                        <tr key={category}>
                          <td style={{ fontWeight: '600', color: '#374151', padding: '15px 20px', background: '#fafafa' }}>{category}</td>
                          {products.map((product, idx) => (
                            <td key={idx} style={{ textAlign: 'center', padding: '15px' }}>
                              {hasService(product, category.split(' ')[0]) ? (
                                <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: '24px' }}></i>
                              ) : (
                                <i className="fas fa-times-circle" style={{ color: '#ef4444', fontSize: '24px' }}></i>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}

                      {['Surgery', 'Immunizations', 'Dental Care'].map((category) => (
                        <tr key={category}>
                          <td style={{ fontWeight: '600', color: '#374151', padding: '15px 20px', background: '#fafafa' }}>{category}</td>
                          {products.map((product, idx) => {
                            const narration = getServiceNarration(product, category);
                            return (
                              <td key={idx} style={{ textAlign: 'center', padding: '15px', fontSize: '0.85rem', color: '#4b5563' }}>
                                {narration || 'Covered to approved limits'}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && products.length > 0 && totalPages > 1 && (
            <div className="row justify-content-center mt-5">
              <div className="col-auto">
                <nav aria-label="Products pagination">
                  <ul className="pagination pagination-lg" style={{ gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                          borderRadius: '10px', padding: '10px 16px', border: '2px solid #e5e7eb',
                          background: currentPage === 1 ? '#f3f4f6' : 'white', color: currentPage === 1 ? '#9ca3af' : '#374151',
                          fontWeight: '600', cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '4px'
                        }}
                        onMouseOver={(e) => { if (currentPage !== 1) { e.currentTarget.style.background = '#fef3c7'; e.currentTarget.style.borderColor = '#F89423'; } }}
                        onMouseOut={(e) => { if (currentPage !== 1) { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e5e7eb'; } }}
                      >
                        <i className="fas fa-chevron-left" style={{ fontSize: '12px' }}></i> Prev
                      </button>
                    </li>

                    {getPageNumbers().map((page, idx) => (
                      <li key={idx} className="page-item">
                        {page === '...' ? (
                          <span className="page-link" style={{ 
                            borderRadius: '10px', padding: '10px 16px', border: 'none', 
                            background: 'transparent', color: '#9ca3af', fontWeight: '600', cursor: 'default' 
                          }}>...</span>
                        ) : (
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(page)}
                            style={{
                              borderRadius: '10px', padding: '10px 16px', 
                              border: `2px solid ${currentPage === page ? '#F89423' : '#e5e7eb'}`,
                              background: currentPage === page ? '#F89423' : 'white', 
                              color: currentPage === page ? 'white' : '#374151',
                              fontWeight: '600', cursor: 'pointer',
                              transition: 'all 0.2s', minWidth: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}
                            onMouseOver={(e) => { if (currentPage !== page) { e.currentTarget.style.background = '#fef3c7'; e.currentTarget.style.borderColor = '#F89423'; } }}
                            onMouseOut={(e) => { if (currentPage !== page) { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e5e7eb'; } }}
                          >
                            {page}
                          </button>
                        )}
                      </li>
                    ))}

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{
                          borderRadius: '10px', padding: '10px 16px', border: '2px solid #e5e7eb',
                          background: currentPage === totalPages ? '#f3f4f6' : 'white', color: currentPage === totalPages ? '#9ca3af' : '#374151',
                          fontWeight: '600', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '4px'
                        }}
                        onMouseOver={(e) => { if (currentPage !== totalPages) { e.currentTarget.style.background = '#fef3c7'; e.currentTarget.style.borderColor = '#F89423'; } }}
                        onMouseOut={(e) => { if (currentPage !== totalPages) { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e5e7eb'; } }}
                      >
                        Next <i className="fas fa-chevron-right" style={{ fontSize: '12px' }}></i>
                      </button>
                    </li>
                  </ul>
                </nav>
                
                <div className="text-center mt-3">
                  <small className="text-muted">
                    Page {currentPage} of {totalPages} • Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, products.length)} of {products.length} products
                  </small>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal */}
        {modalOpen && selectedProduct && (
          <div 
            className="modal-overlay"
            onClick={closeProductModal}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '20px', animation: 'fadeIn 0.2s ease', backdropFilter: 'blur(4px)'
            }}
          >
            <div 
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white', borderRadius: '20px', width: '100%', maxWidth: '750px',
                maxHeight: '90vh', overflowY: 'auto', position: 'relative',
                animation: 'slideUp 0.3s ease', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                borderTop: '5px solid #F89423'
              }}
            >
              <div className="modal-header" style={{ 
                padding: '30px 35px', borderBottom: '1px solid #f0f0f0', 
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <h3 className="mb-0" style={{ fontWeight: '700', color: '#1f2937', fontSize: '1.75rem' }}>
                  {selectedProduct.name || 'Product Details'}
                </h3>
                
                <button 
                  onClick={closeProductModal}
                  style={{
                    background: 'none', border: 'none', fontSize: '28px', color: '#6b7280', cursor: 'pointer',
                    width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '50%', transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#1f2937'; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#6b7280'; }}
                >
                  ×
                </button>
              </div>

              <div className="modal-body" style={{ padding: '35px' }}>
                {(() => {
                  const validCosts = getValidCostAnalysis(selectedProduct.costAnalysis);
                  const defaultTierModal = validCosts.length > 0 ? validCosts[0].familyType : 'Standard';
                  const currentTierModal = selectedTiers[selectedProduct.id] || defaultTierModal;
                  const enrollmentRouteModal = getEnrollmentRoute(currentTierModal);
                  const isIndividualModal = currentTierModal.toLowerCase().includes('individual') || currentTierModal.toLowerCase().includes('myself');

                  return validCosts.length > 0 ? (
                    <div className="mb-5">
                      <h5 className="mb-4" style={{ fontWeight: '700', color: '#1f2937', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className="fas fa-tags" style={{ color: '#F89423' }}></i>
                        Pricing Tiers
                      </h5>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                        {validCosts.map((cost, idx) => (
                          <div 
                            key={idx} 
                            onClick={() => handleTierChange(selectedProduct.id, cost.familyType)}
                            style={{ 
                              background: '#fff7ed', 
                              borderRadius: '8px', 
                              padding: '16px', 
                              border: currentTierModal === cost.familyType ? '2px solid #F89423' : '1px solid #fed7aa',
                              textAlign: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          >
                            <div style={{ fontWeight: '700', color: '#1f2937', fontSize: '1rem', marginBottom: '4px' }}>
                              {cost.familyType || 'Standard Plan'}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '8px' }}>
                              {cost.numberOfFamilyMembers} Member{cost.numberOfFamilyMembers > 1 ? 's' : ''}
                            </div>
                            <div style={{ fontWeight: '800', color: '#F89423', fontSize: '1.2rem' }}>
                              ₦{Number(cost.amounts || 0).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div style={{ marginTop: '20px' }}>
                        <label style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                          Select Coverage Type to Proceed:
                        </label>
                        <select 
                          value={currentTierModal}
                          onChange={(e) => handleTierChange(selectedProduct.id, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            fontSize: '0.95rem',
                            color: '#374151',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            outline: 'none'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#F89423'}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        >
                          {validCosts.map((cost, idx) => (
                            <option key={idx} value={cost.familyType}>
                              {cost.familyType} - ₦{Number(cost.amounts || 0).toLocaleString()} ({cost.numberOfFamilyMembers} Member{cost.numberOfFamilyMembers > 1 ? 's' : ''})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex justify-content-between align-items-center mb-5 p-4" style={{ background: '#fff7ed', borderRadius: '12px', border: '1px solid #fed7aa' }}>
                      <div>
                        <small className="text-muted d-block mb-1" style={{ fontWeight: '500' }}>Starting from</small>
                        <h4 className="mb-0" style={{ color: '#F89423', fontWeight: '800', fontSize: '2rem' }}>
                          ₦{Number(selectedProduct.amounts || 0).toLocaleString()}
                        </h4>
                      </div>
                    </div>
                  );
                })()}

                {selectedProduct.benefits && selectedProduct.benefits !== 'test' && selectedProduct.benefits !== 'tesst' && (
                  <div className="mb-5">
                    <h5 className="mb-4" style={{ fontWeight: '700', color: '#1f2937', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <i className="fas fa-star" style={{ color: '#F89423' }}></i> All Benefits
                    </h5>
                    <ul className="list-unstyled" style={{ display: 'grid', gap: '12px' }}>
                      {formatContentList(selectedProduct.benefits).map((benefit, i) => (
                        <li key={i} className="d-flex align-items-start gap-3" style={{ fontSize: '1rem', color: '#4b5563' }}>
                          <i className="fas fa-check-circle" style={{ color: '#F89423', fontSize: '18px', marginTop: '2px', flexShrink: 0 }}></i>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedProduct.serviceDetails && selectedProduct.serviceDetails.length > 0 && (
                  <div className="mb-5">
                    <h5 className="mb-4" style={{ fontWeight: '700', color: '#1f2937', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <i className="fas fa-file-invoice-dollar" style={{ color: '#F89423' }}></i> Complete Service Details & Coverage
                    </h5>
                    
                    {(() => {
                      const groupedDetails = selectedProduct.serviceDetails.reduce((acc, detail) => {
                        const sName = detail.serviceName || 'General Service';
                        if (!acc[sName]) acc[sName] = [];
                        acc[sName].push(detail);
                        return acc;
                      }, {});

                      return Object.entries(groupedDetails).map(([serviceName, details], groupIndex) => (
                        <div key={groupIndex} className="mb-4">
                          <h6 className="fw-bold mb-3 px-3 py-2" style={{ color: '#F89423', backgroundColor: '#fff7ed', borderRadius: '8px', borderLeft: '4px solid #F89423', fontSize: '1.05rem' }}>
                            {serviceName}
                          </h6>
                          
                          <div className="rounded-3" style={{ border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                            <table className="table table-hover mb-0" style={{ fontSize: '0.95rem' }}>
                              <thead style={{ background: '#f9fafb' }}>
                                <tr>
                                  <th style={{ fontWeight: '600', border: 'none', width: '50%', color: '#374151', padding: '12px 20px' }}>Service Description</th>
                                  <th style={{ fontWeight: '600', border: 'none', width: '50%', color: '#374151', padding: '12px 20px' }}>Narration</th>
                                </tr>
                              </thead>
                              <tbody>
                                {details.map((detail, idx) => (
                                  <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.2s ease' }} onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'} onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                                    <td style={{ padding: '14px 20px', color: '#4b5563', verticalAlign: 'middle', fontWeight: '500' }}>{detail.descriptionName || 'General Service'}</td>
                                    <td style={{ padding: '14px 20px', color: '#6b7280', verticalAlign: 'middle', lineHeight: '1.5' }}>{detail.naration || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}

                {selectedProduct.policyDocument && selectedProduct.policyDocument !== `Policy document for product ${selectedProduct.id}` && (
                  <div className="mb-4">
                    <div className="p-5 text-center" style={{ background: '#fff7ed', borderRadius: '12px', border: '1px solid #fed7aa' }}>
                      <i className="fas fa-file-pdf mb-3" style={{ fontSize: '48px', color: '#F89423' }}></i>
                      <h6 className="mb-2 fw-bold" style={{ color: '#92400e', fontSize: '1.1rem' }}>Policy Document Available</h6>
                      <p className="text-muted small mb-4">Download or view the full terms and conditions for this plan.</p>
                      <div className="d-flex justify-content-center gap-3 flex-wrap">
                        <button onClick={async () => { setIsDownloadingPdf(true); try { await downloadPDF(selectedProduct.policyDocument, `${selectedProduct.name}_Policy.pdf`); } finally { setIsDownloadingPdf(false); } }} disabled={isDownloadingPdf || isViewingPdf} style={{ padding: '12px 24px', borderRadius: '8px', fontWeight: '600', background: '#F89423', color: 'white', border: 'none', cursor: (isDownloadingPdf || isViewingPdf) ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s', opacity: (isDownloadingPdf || isViewingPdf) ? 0.7 : 1 }} onMouseOver={(e) => { if (!isDownloadingPdf && !isViewingPdf) e.currentTarget.style.background = '#ea7c1f'; }} onMouseOut={(e) => { if (!isDownloadingPdf && !isViewingPdf) e.currentTarget.style.background = '#F89423'; }}>
                          {isDownloadingPdf ? <><i className="fas fa-spinner fa-spin"></i> Downloading...</> : <><i className="fas fa-download"></i> Download PDF</>}
                        </button>
                        
                        <button onClick={async () => { setIsViewingPdf(true); try { await viewPDF(selectedProduct.policyDocument); } finally { setIsViewingPdf(false); } }} disabled={isDownloadingPdf || isViewingPdf} style={{ padding: '12px 24px', borderRadius: '8px', fontWeight: '600', background: 'white', color: '#F89423', border: '2px solid #F89423', cursor: (isDownloadingPdf || isViewingPdf) ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s', opacity: (isDownloadingPdf || isViewingPdf) ? 0.7 : 1 }} onMouseOver={(e) => { if (!isDownloadingPdf && !isViewingPdf) e.currentTarget.style.background = '#fff7ed'; }} onMouseOut={(e) => { if (!isDownloadingPdf && !isViewingPdf) e.currentTarget.style.background = 'white'; }}>
                          {isViewingPdf ? <><i className="fas fa-spinner fa-spin"></i> Opening...</> : <><i className="fas fa-eye"></i> View in Browser</>}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer" style={{ padding: '25px 35px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button onClick={closeProductModal} style={{ padding: '12px 28px', borderRadius: '8px', fontWeight: '600', background: '#f3f4f6', color: '#374151', border: 'none', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'} onMouseOut={(e) => e.currentTarget.style.background = '#f3f4f6'}>Close</button>
                
                {(() => {
                  const validCostsModal = getValidCostAnalysis(selectedProduct.costAnalysis);
                  const defaultTierModalFooter = validCostsModal.length > 0 ? validCostsModal[0].familyType : 'Standard';
                  const currentTierModalFooter = selectedTiers[selectedProduct.id] || defaultTierModalFooter;
                  const enrollmentRouteModalFooter = getEnrollmentRoute(currentTierModalFooter);
                  const isIndividualModalFooter = currentTierModalFooter.toLowerCase().includes('individual') || currentTierModalFooter.toLowerCase().includes('myself');

                  return (
                    <Link 
                      to={`${enrollmentRouteModalFooter}?planId=${selectedProduct.id}&planName=${encodeURIComponent(selectedProduct.name)}&planType=${encodeURIComponent(currentTierModalFooter)}`}
                      onClick={() => {
                        const selectedCostModal = validCostsModal.length > 0 
                          ? (validCostsModal.find(c => c.familyType === currentTierModalFooter) || validCostsModal[0]) 
                          : null;
                        const planData = {
                          id: selectedProduct.id,
                          name: selectedProduct.name,
                          type: currentTierModalFooter,
                          price: selectedCostModal ? selectedCostModal.amounts : selectedProduct.amounts
                        };
                        sessionStorage.setItem('selectedPlan', JSON.stringify(planData));
                      }}
                      style={{ 
                        padding: '12px 28px', borderRadius: '8px', fontWeight: '600', 
                        background: '#F89423', color: 'white', border: 'none', 
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                        textDecoration: 'none', boxShadow: '0 4px 12px rgba(248, 148, 35, 0.3)',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.background = '#ea7c1f'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = '#F89423'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                      {isIndividualModalFooter ? 'Get Started' : 'Buy Plan'}
                      <i className={`fas ${isIndividualModalFooter ? 'fa-user-plus' : 'fa-shopping-cart'}`} style={{ fontSize: '14px' }}></i>
                    </Link>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </section>

      <Footer />
    </>
  );
}

export default AllPlans;